import React, { useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  Paper,
  Stack,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  Button,
  IconButton,
  Chip,
} from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { format } from 'date-fns';
import { PICKER_DATE_FORMAT } from '../../../utils/formatTime';
import { AVAILABLE_OCCASIONS, type BulkSpecialHoursEditorProps, type OccasionTypeFromTypes } from './types';
import { formatOccasionName } from '../../StoreHours/types';

export const BulkSpecialHoursEditor: React.FC<BulkSpecialHoursEditorProps> = ({
  entries,
  onEntriesChange,
}) => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isClosed, setIsClosed] = useState(true);
  const [openTime, setOpenTime] = useState('09:00');
  const [closeTime, setCloseTime] = useState('22:00');
  const [occasions, setOccasions] = useState<OccasionTypeFromTypes[]>([...AVAILABLE_OCCASIONS]);

  const handleAddEntry = () => {
    if (!startDate || !endDate || occasions.length === 0) return;
    // Use format() to get YYYY-MM-DD in local timezone, not toISOString() which converts to UTC
    // This prevents timezone-related date shifts for users in different timezones
    onEntriesChange([...entries, {
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
      isClosed,
      openTime: isClosed ? undefined : openTime,
      closeTime: isClosed ? undefined : closeTime,
      occasions: [...occasions],
    }]);
    // Reset form
    setStartDate(null);
    setEndDate(null);
    setIsClosed(true);
    setOpenTime('09:00');
    setCloseTime('22:00');
    setOccasions([...AVAILABLE_OCCASIONS]);
  };

  const handleRemoveEntry = (index: number) => {
    onEntriesChange(entries.filter((_, i) => i !== index));
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Add special hours (holidays, closures, modified hours) to selected stores. You can choose which occasions each entry applies to.
      </Alert>

      {/* Added special hours entries */}
      {entries.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" gutterBottom>
            Special Hours to Add ({entries.length})
          </Typography>
          <Stack spacing={1}>
            {entries.map((entry, index) => (
              <Paper key={index} variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {/* Append T00:00:00 to parse as local date, not UTC */}
                    {format(new Date(entry.startDate + 'T00:00:00'), 'd MMM yyyy')}
                    {entry.startDate !== entry.endDate && ` - ${format(new Date(entry.endDate + 'T00:00:00'), 'd MMM yyyy')}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {entry.isClosed ? 'Closed' : `${entry.openTime} - ${entry.closeTime}`}
                    {' \u2022 '}
                    {entry.occasions.length === AVAILABLE_OCCASIONS.length
                      ? 'All occasions'
                      : entry.occasions.map(formatOccasionName).join(', ')}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveEntry(index)}
                  color="error"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Paper>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Add new special hours entry form */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Add Special Hours Entry
        </Typography>

        {/* Quick presets */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            Quick Presets
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
            <Chip
              label="Today"
              onClick={() => {
                const today = new Date();
                setStartDate(today);
                setEndDate(today);
              }}
              variant="outlined"
              size="small"
            />
            <Chip
              label="Tomorrow"
              onClick={() => {
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                setStartDate(tomorrow);
                setEndDate(tomorrow);
              }}
              variant="outlined"
              size="small"
            />
            <Chip
              label="Christmas Day"
              onClick={() => {
                const year = new Date().getMonth() === 11 && new Date().getDate() > 25
                  ? new Date().getFullYear() + 1
                  : new Date().getFullYear();
                const christmas = new Date(year, 11, 25);
                setStartDate(christmas);
                setEndDate(christmas);
                setIsClosed(true);
              }}
              variant="outlined"
              size="small"
            />
            <Chip
              label="New Year's Day"
              onClick={() => {
                const year = new Date().getMonth() === 0 && new Date().getDate() === 1
                  ? new Date().getFullYear()
                  : new Date().getFullYear() + 1;
                const newYear = new Date(year, 0, 1);
                setStartDate(newYear);
                setEndDate(newYear);
                setIsClosed(true);
              }}
              variant="outlined"
              size="small"
            />
            <Chip
              label="Thanksgiving"
              onClick={() => {
                // 4th Thursday of November
                const year = new Date().getMonth() > 10
                  ? new Date().getFullYear() + 1
                  : new Date().getFullYear();
                const nov = new Date(year, 10, 1);
                const dayOfWeek = nov.getDay();
                const firstThursday = dayOfWeek <= 4 ? 4 - dayOfWeek + 1 : 11 - dayOfWeek + 4 + 1;
                const thanksgiving = new Date(year, 10, firstThursday + 21);
                setStartDate(thanksgiving);
                setEndDate(thanksgiving);
                setIsClosed(true);
              }}
              variant="outlined"
              size="small"
            />
          </Stack>
        </Box>

        <Stack spacing={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  format={PICKER_DATE_FORMAT}
                  label="Start Date *"
                  value={startDate}
                  onChange={(newValue) => {
                    setStartDate(newValue);
                    // If end date is not set or is before start date, set it to start date
                    if (!endDate || (newValue && endDate < newValue)) {
                      setEndDate(newValue);
                    }
                  }}
                  views={['year', 'month', 'day']}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  format={PICKER_DATE_FORMAT}
                  label="End Date *"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  views={['year', 'month', 'day']}
                  minDate={startDate || undefined}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>

          <FormControlLabel
            control={
              <Switch
                checked={isClosed}
                onChange={(e) => setIsClosed(e.target.checked)}
              />
            }
            label="Store Closed"
          />

          {!isClosed && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Open Time"
                  type="time"
                  value={openTime}
                  onChange={(e) => setOpenTime(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Close Time"
                  type="time"
                  value={closeTime}
                  onChange={(e) => setCloseTime(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          )}

          {/* Occasion Selection */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Apply to Occasions
              </Typography>
              {occasions.length < AVAILABLE_OCCASIONS.length && (
                <Button
                  size="small"
                  onClick={() => setOccasions([...AVAILABLE_OCCASIONS])}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0 }}
                >
                  Select All
                </Button>
              )}
            </Box>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {AVAILABLE_OCCASIONS.map((occasion) => (
                <Chip
                  key={occasion}
                  label={occasion.replace('_', ' ')}
                  size="small"
                  variant={occasions.includes(occasion) ? 'filled' : 'outlined'}
                  color={occasions.includes(occasion) ? 'primary' : 'default'}
                  onClick={() => {
                    setOccasions(prev => {
                      if (prev.includes(occasion)) {
                        // Don't allow deselecting all
                        if (prev.length === 1) return prev;
                        return prev.filter(o => o !== occasion);
                      }
                      return [...prev, occasion];
                    });
                  }}
                />
              ))}
            </Stack>
            {occasions.length < AVAILABLE_OCCASIONS.length && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Other occasions will keep their regular hours
              </Typography>
            )}
          </Box>

          <Button
            variant="outlined"
            onClick={handleAddEntry}
            disabled={!startDate || !endDate || occasions.length === 0}
          >
            Add Entry
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};
